import clsx from "clsx";
import {
  createChart,
  CrosshairMode,
  ColorType,
  PriceScaleMode,
  BarPrice,
  LineData,
  IChartApi,
  ISeriesApi,
  LineStyle,
  LineType,
  TrackingModeExitMode,
  Time,
  LastPriceAnimationMode,
} from "lightweight-charts";
import moment from "moment";
import { useTheme } from "next-themes";
import { FC, useCallback, useEffect, useMemo, useRef } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { usePathname } from "@/lib/i18n";
import { formatNumber, percentageFormat, shortNumberFormat } from "@/lib/utils";
import * as m from "@/paraglide/messages";
import useScreenSize from "@/services/hooks/screen.hooks";

const primarySeriesColor = "#16a34a";

export interface GraphChartType {
  primaryLineData: chartDataType[];
  secondaryLineData?: chartDataType[];
  className?: string;
  fixed?: boolean;
  isLoading?: boolean;
  responsive?: boolean;
  tooltip: {
    show: boolean;
    title: string;
    showProfitChange?: boolean;
    showValue?: boolean;
  };
  isUSD?: boolean;
}

export interface chartDataType {
  time: Time;
  value?: number;
  open?: number;
  close?: number;
  high?: number;
  low?: number;
}

const LineChart: FC<GraphChartType> = ({
  primaryLineData,
  secondaryLineData,
  className,
  fixed = true,
  isLoading = false,
  tooltip,
  isUSD = true,
}) => {
  const pathname = usePathname();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const tooltipContainerRef = useRef<HTMLDivElement>(null);
  const { width } = useScreenSize();
  const { theme } = useTheme();

  const getTooltipUI = useCallback(
    (pointValue: number, prevPointValue: number) => {
      const profitChange = `
        <span className="text-lg" >
          ${percentageFormat(
            ((pointValue - prevPointValue) / prevPointValue) * 100,
            true,
          )}%
        </span>
    `;

      const value = `
        <span classname="italic" >
          ${formatNumber(pointValue, isUSD)}
        </span>
    `;

      return `
      <div className="flex gap-4">
      ${tooltip.showValue ? value : ""}
        ${tooltip.showProfitChange ? profitChange : ""}
        </div>
        `;
    },
    [tooltip.showProfitChange, tooltip.showValue, isUSD],
  );
  const highestPoint: any = useMemo(
    () =>
      Array.isArray(primaryLineData) && primaryLineData.length
        ? primaryLineData?.reduce((prev, curr) =>
            (curr.value ?? 0) > (prev.value ?? 0) ? curr : prev,
          )
        : {},
    [primaryLineData],
  );
  const lowestPoint: any = useMemo(
    () =>
      Array.isArray(primaryLineData) && primaryLineData.length
        ? primaryLineData?.reduce((prev, curr) =>
            (curr.value ?? 0) < (prev.value ?? 0) ? curr : prev,
          )
        : {},
    [primaryLineData],
  );

  const manageTooltip = useCallback(
    (
      chart: IChartApi,
      primarySeries: ISeriesApi<"Area">,
      secondarySeries: ISeriesApi<"Area">,
    ) => {
      if (tooltip.show) {
        const toolTipWidth = 96;
        const toolTipHeight = 100;
        const toolTipMargin = 15;

        if (tooltipContainerRef.current) {
          if (chartContainerRef.current)
            chartContainerRef.current.appendChild(tooltipContainerRef.current);
        }
        if (
          highestPoint?.time &&
          lowestPoint?.time &&
          !pathname.includes("test")
        ) {
          const markers = [
            {
              time: lowestPoint.time,
              position: "belowBar",
              color: theme === "dark" ? "white" : "black",
              size: 0.5,
              shape: "circle",
              text: `L: ${formatNumber(lowestPoint.value, true)}`,
              id: "lowest",
            },
            {
              time: highestPoint.time,
              position: "aboveBar",
              color: theme === "dark" ? "white" : "black",
              size: 0.5,
              shape: "circle",
              text: `H:${formatNumber(highestPoint.value, true)}`,
              id: "highest",
            },
          ].sort((a, b) => (a.time > b.time ? 1 : -1));
          primarySeries.setMarkers(markers as any);
        }
        chart.subscribeCrosshairMove((param) => {
          if (tooltipContainerRef.current && chartContainerRef.current) {
            if (
              param.point === undefined ||
              !param.time ||
              param.point.x < 0 ||
              param.point.x > chartContainerRef.current.clientWidth ||
              param.point.y < 0 ||
              param.point.y > chartContainerRef.current.clientHeight
            ) {
              tooltipContainerRef.current.style.display = "none";
            } else {
              tooltipContainerRef.current.style.display = "block";

              const primaryLine: LineData = param.seriesData.get(
                primarySeries,
              ) as LineData;
              const dateStr = moment(new Date((param.time as number) * 1000))
                .utcOffset(0)
                .format("DD MMM YYYY HH:mm");
              const secondaryLine: LineData = param.seriesData.get(
                secondarySeries,
              ) as LineData;
              const primaryPrice = primaryLine?.value ?? 0;
              const secondaryPrice = secondaryLine?.value ?? 0;

              let primaryPrevPointPrice = primaryLineData[0]?.value ?? 0;

              let secondaryPrevPointPrice = secondaryLineData
                ? secondaryLineData[0]?.value ?? 0
                : 0;

              const secondaryDataTooltip = secondaryLineData
                ? getTooltipUI(secondaryPrice, secondaryPrevPointPrice)
                : "";

              tooltipContainerRef.current.innerHTML = `
                              <p class="text-xs font-mono text-primary">${dateStr}</p>
                                  ${getTooltipUI(
                                    primaryPrice,
                                    primaryPrevPointPrice,
                                    // theme === "light"

                                    // : "white",
                                  )}
                                  ${secondaryDataTooltip}

                              `;

              const coordinate = primarySeries.priceToCoordinate(primaryPrice);
              if (coordinate === null) {
                return;
              }

              let left = param.point.x - toolTipWidth / 2;
              if (left < 0) {
                left = toolTipMargin;
              }
              if (left > chartContainerRef.current.clientWidth - toolTipWidth) {
                left =
                  chartContainerRef.current.clientWidth -
                  toolTipWidth -
                  toolTipMargin;
              }

              const top =
                coordinate - toolTipHeight - toolTipMargin > 0
                  ? coordinate - toolTipHeight - toolTipMargin
                  : coordinate + toolTipMargin;

              tooltipContainerRef.current.style.left = `${left}px`;
              tooltipContainerRef.current.style.top = `${top}px`;
            }
          }
        });
      }
    },
    [
      tooltip.show,
      highestPoint.time,
      highestPoint.value,
      lowestPoint.time,
      lowestPoint.value,
      pathname,
      primaryLineData,
      secondaryLineData,
      getTooltipUI,
      theme,
    ],
  );

  useEffect(() => {
    if (!chartContainerRef?.current || !primaryLineData) {
      return;
    }

    const handleResize = () => {
      chart.applyOptions({ width: chartContainerRef?.current?.clientWidth });
    };

    const chart = createChart(chartContainerRef?.current, {
      width: chartContainerRef?.current?.clientWidth,
      height: chartContainerRef?.current?.clientHeight,
      layout: {
        background: {
          type: ColorType.Solid,
          // color: theme === "light" ? "#000" : "#f8fafc",
          color: "#00000000",
        },
        textColor: theme === "light" ? "#000" : "#f8fafc",
        fontSize: 10,
        attributionLogo: false,
        fontFamily: "monospace",
      },
      grid: {
        vertLines: {
          visible: false,
        },
        horzLines: {
          visible: false,
          // color: theme === "light" ? "#f2f2f2" : "#0002",
          color: "#f2f2f2",
        },
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        horzLine: {
          visible: true,
        },
        vertLine: {
          labelVisible: false,
        },
      },
      leftPriceScale: {
        borderVisible: false,
        autoScale: true,
        visible: width > 450,
        mode: PriceScaleMode.Normal,
        textColor: theme === "light" ? "#000" : "#f8fafc",
      },
      rightPriceScale: {
        visible: false,
      },
      timeScale: {
        borderColor: "transparent",
        visible: width > 450,
        timeVisible: width > 450,
        secondsVisible: false,
      },
      handleScale: !fixed,
      handleScroll: !fixed,
      trackingMode: {
        exitMode: TrackingModeExitMode.OnNextTap,
      },
    });

    chart.timeScale().fitContent();

    const series = chart.addAreaSeries({
      lineColor: primarySeriesColor,
      topColor: primarySeriesColor + "5f",
      bottomColor: primarySeriesColor + "00",
      lineWidth: 2,
      crosshairMarkerVisible: true,
      crosshairMarkerBackgroundColor: primarySeriesColor,
      crosshairMarkerRadius: 5,
      priceLineVisible: true,
      lastValueVisible: true,
      lineStyle: LineStyle.Solid,
      lineType: LineType.Curved,
      priceFormat: {
        type: "custom",
        formatter: (priceValue: BarPrice) => {
          return `$${shortNumberFormat(priceValue)}`;
        },
      },
      lastPriceAnimation: LastPriceAnimationMode.Continuous,
    });

    series.setData(
      primaryLineData.map((d: any) => ({ time: d.time, value: d.value })),
    );

    const secondarySeriesColor = "#f59e0b";
    const series2 = chart.addAreaSeries({
      lineColor: secondarySeriesColor,
      topColor: secondarySeriesColor + "0f",
      bottomColor: secondarySeriesColor + "00",
      lineWidth: 2,
      crosshairMarkerVisible: true,
      crosshairMarkerBackgroundColor: secondarySeriesColor,
      crosshairMarkerRadius: 5,
      priceLineVisible: false,
      lastValueVisible: false,
      lineStyle: LineStyle.Solid,
      lineType: LineType.Curved,
      autoscaleInfoProvider: () => ({
        priceRange: secondaryLineData
          ? {
              minValue:
                Math.min(
                  ...(secondaryLineData.map((d) => d.value) as number[]),
                ) * 0.98,
              maxValue:
                Math.max(
                  ...(secondaryLineData.map((d) => d.value) as number[]),
                ) * 1.02,
            }
          : null,
      }),
      priceFormat: {
        type: "custom",
        formatter: (priceValue: BarPrice) => {
          return `$${shortNumberFormat(priceValue)}`;
        },
      },
      lastPriceAnimation: LastPriceAnimationMode.Continuous,
    });
    series2.setData(secondaryLineData ?? []);

    window.addEventListener("resize", handleResize);

    manageTooltip(chart, series, series2);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.remove();
    };
  }, [
    fixed,
    isUSD,
    manageTooltip,
    primaryLineData,
    secondaryLineData,
    theme,
    width,
  ]);

  const loadingOverlay = useMemo(() => {
    if (isLoading) {
      return (
        <div className="flex flex-col flex-1 items-center justify-center backdrop-blur-sm bg-white bg-opacity-30  absolute w-full h-full z-10">
          <p className="text-xl font-mono ">{m.ok_front_grebe_trip()}</p>
        </div>
      );
    }
  }, [isLoading]);

  const tooltipContainer = useMemo(() => {
    return (
      <div
        className="border bg-background   absolute top-0 left-0 z-50 hidden p-4 pointer-events-none "
        ref={tooltipContainerRef}
        style={{
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        }}
      ></div>
    );
  }, []);

  return primaryLineData ? (
    <div
      ref={chartContainerRef}
      className={clsx(
        "relative flex flex-col flex-1 md:min-h-full min-h-[260px]",
        className,
      )}
    >
      {loadingOverlay}
      {tooltipContainer}
    </div>
  ) : isLoading ? (
    <Skeleton className="w-full h-full" />
  ) : (
    <></>
  );
};

export default LineChart;
